import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ScrollReveal from '../components/ScrollReveal';
import TeamCarousel from '../components/TeamCarousel';
import { teamMembers, studioStats } from '../data/mockData';

const About = () => (
  <main className="pt-24 pb-0">
    {/* Header */}
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-20">
      <ScrollReveal>
        <span className="text-[#5f6368] text-xs tracking-wide uppercase">About Us</span>
        <h1
          className="text-4xl md:text-5xl lg:text-6xl text-[#1a1a1a] mt-4 tracking-tight max-w-4xl"
          style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
        >
          Design with purpose, craft with intention
        </h1>
      </ScrollReveal>
    </section>

    {/* Story */}
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <ScrollReveal>
          <div className="rounded-2xl overflow-hidden border border-[#e8eaed]">
            <img
              src="https://images.unsplash.com/photo-1742440710226-450e3b85c100?w=800&h=600&fit=crop"
              alt="Grapix Studio"
              className="w-full h-full object-cover"
            />
          </div>
        </ScrollReveal>
        <ScrollReveal delay={200}>
          <div>
            <h2
              className="text-3xl md:text-4xl text-[#1a1a1a] mb-6 tracking-tight"
              style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
            >
              Our Story
            </h2>
            <div className="space-y-5 text-[#5f6368] text-base leading-relaxed">
              <p>
                Founded in 2020, Grapix was born from a simple belief: great design is the most powerful tool a brand can wield. What started as a two-person studio in San Francisco has grown into a team of passionate creatives dedicated to helping brands find their visual voice.
              </p>
              <p>
                We approach every project as a unique challenge, blending strategic thinking with artistic craft to deliver work that not only looks exceptional but drives real results. From startups finding their first customers to established brands evolving for new audiences, we've partnered with over 85 clients across diverse industries.
              </p>
              <p>
                Our philosophy is rooted in collaboration, attention to detail, and an unwavering commitment to pushing creative boundaries while maintaining clarity and purpose.
              </p>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>

    {/* Values */}
    <section className="bg-[#f8f9fa] py-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <ScrollReveal>
          <span className="text-[#5f6368] text-xs tracking-wide uppercase">Our Principles</span>
          <h2
            className="text-3xl md:text-4xl text-[#1a1a1a] mt-3 mb-16 tracking-tight"
            style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
          >
            What guides our craft
          </h2>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: 'Strategic First',
              desc: 'Every design decision is rooted in strategy. We dig deep to understand your audience, market, and goals before a single pixel is placed.'
            },
            {
              title: 'Craft Obsessed',
              desc: 'We sweat the details. From kerning to color, every element is meticulously refined to create work that feels considered and intentional.'
            },
            {
              title: 'Future Thinking',
              desc: 'We design for longevity. Our work is built to evolve with your brand, adapting to new contexts and audiences while maintaining its core identity.'
            }
          ].map((value, i) => (
            <ScrollReveal key={value.title} delay={i * 150}>
              <div className="bg-white rounded-2xl p-8 border border-[#e8eaed] h-full">
                <span className="text-xs text-[#5f6368] tracking-wide">0{i + 1}</span>
                <h3
                  className="text-xl font-medium text-[#1a1a1a] mt-4 mb-3"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                >
                  {value.title}
                </h3>
                <p className="text-sm text-[#5f6368] leading-relaxed">{value.desc}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>

    {/* Stats */}
    <section className="py-20 border-b border-[#e8eaed]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {studioStats.map((stat, i) => (
            <ScrollReveal key={stat.label} delay={i * 100}>
              <div className="text-center">
                <motion.p
                  className="text-4xl md:text-5xl font-medium text-[#1a1a1a] mb-2"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  {stat.value}
                </motion.p>
                <p className="text-sm text-[#5f6368]">{stat.label}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>

    {/* Team */}
    <section className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <ScrollReveal>
          <span className="text-[#5f6368] text-xs tracking-wide uppercase">The Team</span>
          <h2
            className="text-3xl md:text-4xl text-[#1a1a1a] mt-3 mb-16 tracking-tight"
            style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
          >
            The people behind the pixels
          </h2>
        </ScrollReveal>
        <ScrollReveal delay={200}>
          <TeamCarousel />
        </ScrollReveal>
      </div>
    </section>

    {/* CTA */}
    <section className="bg-[#f8f9fa] py-24 border-t border-[#e8eaed]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
        <ScrollReveal>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl text-[#1a1a1a] mb-8 tracking-tight"
            style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 400 }}
          >
            Ready to start your next project?
          </h2>
          <Link
            to="/consult"
            className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-[#333] transition-colors duration-200"
          >
            Book a Consultation <ArrowUpRight size={16} />
          </Link>
        </ScrollReveal>
      </div>
    </section>
  </main>
);

export default About;
